import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const categories = ['All', 'Web Development', 'Cloud & DevOps', 'Data & AI', 'Mobile', 'UI/UX', 'Business'];

const blogs = [
  {
    category: 'Cloud & DevOps',
    readTime: '8 min read',
    date: 'June 10, 2026',
    gradient: 'from-green-500 to-teal-500',
    title: 'Why Infrastructure as Code is No Longer Optional in 2026',
    excerpt: 'Manual cloud infrastructure management is the leading cause of configuration drift, security misconfigurations, and unpredictable deployments. Here\'s how IaC with Terraform transforms your operations.',
    tags: ['Terraform', 'AWS', 'DevOps'],
    featured: true
  },
  {
    category: 'Data & AI',
    readTime: '6 min read',
    date: 'June 4, 2026',
    gradient: 'from-indigo-500 to-purple-500',
    title: 'Building Real-Time Analytics Pipelines with Apache Kafka and Spark',
    excerpt: 'A practical walkthrough of architecting a sub-second data pipeline that processes millions of events per minute — without breaking the bank.',
    tags: ['Kafka', 'Spark', 'Python'],
    featured: true
  },
  {
    category: 'Web Development',
    readTime: '5 min read',
    date: 'May 28, 2026',
    gradient: 'from-blue-500 to-cyan-500',
    title: 'React Server Components: What They Mean for Your Architecture',
    excerpt: 'Server Components fundamentally change how we think about data fetching and rendering. We break down what this means for performance, SEO, and your existing codebase.',
    tags: ['React', 'Next.js', 'Performance'],
    featured: false
  },
  {
    category: 'Mobile',
    readTime: '7 min read',
    date: 'May 20, 2026',
    gradient: 'from-purple-500 to-pink-500',
    title: 'React Native vs Flutter in 2026: The Definitive Comparison',
    excerpt: 'After building 50+ cross-platform apps, here\'s our honest assessment of when to choose React Native, when Flutter wins, and when you should go native.',
    tags: ['React Native', 'Flutter', 'Mobile'],
    featured: false
  },
  {
    category: 'UI/UX',
    readTime: '4 min read',
    date: 'May 12, 2026',
    gradient: 'from-orange-500 to-red-500',
    title: '10 UX Patterns That Double Conversion Rates (With Data)',
    excerpt: 'We analyzed 50 A/B tests across 20 client products and identified the specific UI patterns that consistently improve sign-up, purchase, and engagement rates.',
    tags: ['Conversion', 'UX Research', 'A/B Testing'],
    featured: false
  },
  {
    category: 'Cloud & DevOps',
    readTime: '9 min read',
    date: 'May 5, 2026',
    gradient: 'from-slate-600 to-blue-600',
    title: 'Zero-Downtime Database Migrations: A Production Playbook',
    excerpt: 'Migrating production databases without downtime is one of the hardest engineering challenges. This playbook covers the exact strategies we use for our enterprise clients.',
    tags: ['Database', 'PostgreSQL', 'Kubernetes'],
    featured: false
  },
  {
    category: 'Data & AI',
    readTime: '6 min read',
    date: 'April 28, 2026',
    gradient: 'from-indigo-500 to-purple-500',
    title: 'From Raw Data to Business Intelligence in 30 Days',
    excerpt: 'A step-by-step guide to building your first data warehouse, connecting your BI tool, and delivering actionable dashboards to your leadership team — in under a month.',
    tags: ['Data Warehouse', 'Power BI', 'dbt'],
    featured: false
  },
  {
    category: 'Business',
    readTime: '5 min read',
    date: 'April 21, 2026',
    gradient: 'from-blue-500 to-cyan-500',
    title: 'Why 70% of Digital Transformation Projects Fail (And How to Be in the 30%)',
    excerpt: 'After working with 200+ companies on digital transformation, we\'ve identified the patterns that predict success — and the common traps that derail even well-funded initiatives.',
    tags: ['Strategy', 'Digital Transformation', 'Leadership'],
    featured: false
  },
  {
    category: 'Web Development',
    readTime: '7 min read',
    date: 'April 14, 2026',
    gradient: 'from-blue-500 to-cyan-500',
    title: 'Core Web Vitals in 2026: What Still Matters for SEO and UX',
    excerpt: 'Google\'s Core Web Vitals continue to evolve. Here\'s a current breakdown of LCP, INP, and CLS thresholds, how to measure them, and the highest-impact fixes for real-world apps.',
    tags: ['Performance', 'SEO', 'JavaScript'],
    featured: false
  }
];

const BlogsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);
  const postsRef = useRef(null);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setTimeout(() => {
      if (postsRef.current) {
        const top = postsRef.current.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const fallback = setTimeout(() => setIsVisible(true), 120);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { clearTimeout(fallback); setIsVisible(true); } },
      { threshold: 0.05 }
    );
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      clearTimeout(fallback);
      if (contentRef.current) observer.unobserve(contentRef.current);
    };
  }, []);

  const filteredBlogs = selectedCategory === 'All'
    ? blogs
    : blogs.filter(b => b.category === selectedCategory);

  const featuredBlogs = blogs.filter(b => b.featured);
  const regularBlogs = filteredBlogs.filter(b => !b.featured);

  return (
    <>
      <title>IT Services &amp; Software Development Blog | Scalizap Kerala, India</title>
      <meta name="description" content="Stay updated with the latest insights on software development, AI, cloud computing, mobile apps, digital transformation, and business technology trends from Scalizap." />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10 text-center text-white">
          <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest mb-4 bg-white/10 px-4 py-1.5 rounded-full">
            Insights & Expertise
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            The Scalizap <span className="gradient-text">Blog</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Deep dives, practical guides, and strategic insights from Scalizap's engineering and product teams.
          </p>
        </div>
      </section>

      <div ref={contentRef}>
        {/* Featured Posts */}
        {selectedCategory === 'All' && (
          <section className="section-padding bg-white">
            <div className="container-custom">
              <div className={`mb-10 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
                <h2 className="font-display text-2xl font-bold text-slate-900">Featured Articles</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {featuredBlogs.map((blog, i) => (
                  <article
                    key={i}
                    className={`bg-slate-50 rounded-2xl overflow-hidden card-hover group cursor-pointer ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <div className={`h-48 bg-gradient-to-br ${blog.gradient} flex items-center justify-center relative overflow-hidden`}>
                      <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-4 right-4 w-32 h-32 bg-white rounded-full blur-2xl"></div>
                        <div className="absolute bottom-4 left-4 w-24 h-24 bg-white rounded-full blur-2xl"></div>
                      </div>
                      <span className="relative z-10 bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">{blog.category}</span>
                    </div>
                    <div className="p-7">
                      <div className="flex items-center gap-3 text-slate-400 text-xs mb-3">
                        <span>{blog.date}</span>
                        <span>·</span>
                        <span>{blog.readTime}</span>
                      </div>
                      <h3 className="font-display font-bold text-xl text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">{blog.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4">{blog.excerpt}</p>
                      <div className="flex flex-wrap gap-2">
                        {blog.tags.map((tag, j) => (
                          <span key={j} className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Category Filter + All Posts */}
        <section className={`section-padding ${selectedCategory === 'All' ? 'bg-slate-50' : 'bg-white'}`}>
          <div className="container-custom">
            {/* Filter */}
            <div className={`flex flex-wrap gap-2 mb-10 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${selectedCategory === cat
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Posts Grid */}
            <div ref={postsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularBlogs.map((blog, i) => (
                <article
                  key={i}
                  className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 card-hover group cursor-pointer ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                  style={{ animationDelay: `${i * 0.07}s` }}
                >
                  <div className={`h-3 bg-gradient-to-r ${blog.gradient}`}></div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">{blog.category}</span>
                      <span className="text-slate-400 text-xs">{blog.readTime}</span>
                    </div>
                    <h3 className="font-display font-bold text-base text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">{blog.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3">{blog.excerpt}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <span className="text-slate-400 text-xs">{blog.date}</span>
                      <span className="text-blue-600 text-xs font-semibold group-hover:underline">Read more →</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredBlogs.length === 0 && (
              <div className="text-center py-20 text-slate-400">
                <p className="text-lg">No articles in this category yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
          <div className="container-custom text-center text-white">
            <div className={`max-w-2xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Get Insights in Your Inbox</h2>
              <p className="text-white/80 text-lg mb-8">
                Join 2,000+ technology leaders who receive Scalizap's monthly digest of industry insights, engineering guides, and practical tutorials.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-1 px-4 py-3.5 rounded-xl bg-white/10 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <button className="bg-white text-blue-600 font-semibold px-6 py-3.5 rounded-xl hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap">
                  Subscribe
                </button>
              </div>
              <p className="text-white/50 text-xs mt-4">No spam. Unsubscribe at any time.</p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default BlogsPage;
